import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Edit, Trash2, Box, DollarSign, Calendar, User, Truck, Wrench, FileText, Package, Search, Eye, ChevronLeft, ChevronRight, Filter, X, ChevronDown } from 'lucide-react';
import ExitForm from '../components/ExitForm';
import { exitsService } from '../services/exitsService';
import api from '../services/api';

const ExitsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exits, setExits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingExit, setEditingExit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredExits, setFilteredExits] = useState([]);
  // La página se deriva de los searchParams: la URL es la única fuente de verdad
  const page = Number(searchParams.get('page') ?? 1);
  const [itemsPerPage] = useState(30);
  const [filters, setFilters] = useState({
    product: '',
    deliveredTo: '',
    workshop: '',
    dateRange: '',
    costRange: '',
    startDate: '',
    endDate: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRefs = useRef({});

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown].contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Cargar salidas al montar el componente
  useEffect(() => {
    console.log('🚀 ExitsPage useEffect ejecutándose...');
    
    const loadExits = async () => {
      try {
        setLoading(true);
        console.log('🔄 Iniciando carga de salidas...');
        
        // Usar los parámetros por defecto del servicio
        const response = await exitsService.getAll(1, 100000);
        console.log('📊 Respuesta salidas:', response);
        
        // Extraer los datos según el formato de respuesta
        let data = [];
        if (response && response.success && response.data) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (response && Array.isArray(response)) {
          data = response;
        }
        
        console.log('✅ Salidas procesadas:', data.length, data);
        setExits(data);
      } catch (error) {
        console.error('❌ Error cargando salidas:', error);
        console.error('❌ Stack trace:', error.stack);
        console.error('❌ Error response:', error.response);
        
        // Intentar cargar salidas vacías para no romper la UI
        setExits([]);
      } finally {
        setLoading(false);
      }
    };

    loadExits();
  }, []);

  // Filtrar salidas basado en el término de búsqueda y filtros
  const prevFiltersRef = useRef({ searchTerm, filters, sortConfig });

  useEffect(() => {
    let filtered = exits;

    // Filtro de búsqueda general
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(exit => {
        // Buscar en campos básicos
        const basicMatch = (exit.product_name || '').toLowerCase().includes(term) ||
          (exit.delivered_to || '').toLowerCase().includes(term) ||
          (exit.requisition_number || '').toLowerCase().includes(term) ||
          (exit.truck || '').toLowerCase().includes(term) ||
          (exit.workshop || '').toLowerCase().includes(term) ||
          (exit.company || '').toLowerCase().includes(term) ||
          (exit.solicitante || '').toLowerCase().includes(term);
        
        // Si ya encontramos coincidencia, retornar true
        if (basicMatch) return true;
        
        // Buscar también en el array de productos si existe
        if (Array.isArray(exit.products) && exit.products.length > 0) {
          return exit.products.some(product => 
            (product.product_name || '').toLowerCase().includes(term) ||
            (product.product_sku || '').toLowerCase().includes(term)
          );
        }
        
        return false;
      });
    }

    // Filtro por producto
    if (filters.product) {
      filtered = filtered.filter(exit =>
        (exit.product_name || '').toLowerCase().includes(filters.product.toLowerCase())
      );
    }

    // Filtro por destinatario
    if (filters.deliveredTo) {
      filtered = filtered.filter(exit =>
        (exit.delivered_to || '').toLowerCase().includes(filters.deliveredTo.toLowerCase())
      );
    }

    // Filtro por taller
    if (filters.workshop) {
      filtered = filtered.filter(exit =>
        (exit.workshop || '').toLowerCase().includes(filters.workshop.toLowerCase())
      );
    }

    // Filtro por rango de fecha personalizado
    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999); // Incluir todo el día final
      filtered = filtered.filter(exit => {
        const exitDate = new Date(exit.exit_date);
        return exitDate >= start && exitDate <= end;
      });
    } else if (filters.startDate) {
      const start = new Date(filters.startDate);
      filtered = filtered.filter(exit => {
        const exitDate = new Date(exit.exit_date);
        return exitDate >= start;
      });
    } else if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(exit => {
        const exitDate = new Date(exit.exit_date);
        return exitDate <= end;
      });
    }

    // Filtro por rango de fecha predefinido
    if (filters.dateRange && !filters.startDate && !filters.endDate) {
      const today = new Date();
      filtered = filtered.filter(exit => {
        const exitDate = new Date(exit.exit_date);
        switch (filters.dateRange) {
          case 'today':
            return exitDate.toDateString() === today.toDateString();
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(today.getDate() - 7);
            return exitDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(today.getMonth() - 1);
            return exitDate >= monthAgo;
          case 'year':
            const yearAgo = new Date(today);
            yearAgo.setFullYear(today.getFullYear() - 1);
            return exitDate >= yearAgo;
          default:
            return true;
        }
      });
    }

    // Filtro por rango de costo
    if (filters.costRange) {
      filtered = filtered.filter(exit => {
        const cost = parseFloat(exit.unit_cost || (exit.products && exit.products.length > 0 ? exit.products[0].unit_cost : 0)) || 0;
        switch (filters.costRange) {
          case 'bajo':
            return cost < 100;
          case 'medio':
            return cost >= 100 && cost < 500;
          case 'alto':
            return cost >= 500;
          default:
            return true;
        }
      });
    }

    // Aplicar ordenamiento
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Manejo especial para fechas
        if (sortConfig.key === 'exit_date') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        // Manejo especial para números
        else if (['quantity', 'unit_cost', 'total_cost'].includes(sortConfig.key)) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        // Manejo especial para strings
        else if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredExits(filtered);

    const filtersChanged =
      prevFiltersRef.current.searchTerm !== searchTerm ||
      prevFiltersRef.current.filters !== filters ||
      prevFiltersRef.current.sortConfig !== sortConfig;

    if (filtersChanged) {
      try {
        if (searchParams.get('page') !== '1') {
          setSearchParams({ page: '1' }, { replace: true });
        }
      } catch (e) {
        console.warn('No se pudo actualizar page en la URL al aplicar filtros', e);
      }
    }

    prevFiltersRef.current = { searchTerm, filters, sortConfig };
  }, [exits, searchTerm, filters, sortConfig]);

  const handleViewExit = (exit) => {
    navigate(`/exits/exit-multi/${exit.id}?page=${page}`);
  };

  const handleAddExit = () => {
    // Navegar a la página de detalle de salida con modo de creación
    navigate('/exits/exit-multi/new');
  };

  const handleEditExit = (exit) => {
    navigate(`/exits/exit-multi/${exit.id}`);
  };

  const handleDeleteExit = (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta salida? ¡Esto afectará tu inventario!')) {
      setExits(prevExits => prevExits.filter(x => x.id !== id));
    }
  };

  const handleSaveExit = (exitData) => {
    if (exitData.id) {
      setExits(prevExits =>
        prevExits.map(x => (x.id === exitData.id ? exitData : x))
      );
    } else {
      const newId = (Math.random() * 100000).toFixed(0);
      setExits(prevExits => [...prevExits, { ...exitData, id: newId }]);
    }
    setShowForm(false);
    setEditingExit(null);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingExit(null);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      product: '',
      deliveredTo: '',
      workshop: '',
      dateRange: '',
      costRange: '',
      startDate: '',
      endDate: ''
    });
    setSortConfig({ key: null, direction: 'asc' });
    setSearchTerm('');
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  // Funciones de ordenamiento
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const toggleDropdown = (columnName) => {
    setActiveDropdown(activeDropdown === columnName ? null : columnName);
  };

  const getUniqueProducts = () => {
    // Obtener productos de exit.product_name o del primer producto en exit.products si existe
    const productNames = exits.map(exit => {
      if (exit.product_name) return exit.product_name;
      if (exit.products && exit.products.length > 0) return exit.products[0].product_name;
      return null;
    }).filter(Boolean);
    
    return [...new Set(productNames)].filter(name => name && name !== 'Material no especificado');
  };

  const getUniqueDeliveredTo = () => {
    const deliveredToList = exits.map(exit => exit.delivered_to).filter(Boolean);
    return [...new Set(deliveredToList)].filter(name => name && name !== 'No especificado');
  };

  const getUniqueWorkshops = () => {
    const workshopList = exits.map(exit => exit.workshop).filter(Boolean);
    return [...new Set(workshopList)].filter(name => name && name !== 'No especificado');
  };

  // Cálculos de paginación
  const totalExits = filteredExits.length;
  const totalPages = Math.ceil(totalExits / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentExits = filteredExits.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setSearchParams({ page: String(newPage) });
  };

  // Si la URL no tiene page al montar, restaurarla desde sessionStorage (solo una vez)
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (!pageParam) {
      const saved = sessionStorage.getItem('exits_page') || '1';
      setSearchParams({ page: saved });
    }
  }, []);

  // Guardar la página actual en sessionStorage cuando cambie (page es la fuente de verdad)
  useEffect(() => {
    try {
      sessionStorage.setItem('exits_page', String(page));
    } catch (e) {
      console.warn('No se pudo guardar exits_page en sessionStorage', e);
    }
  }, [page]);

  // Componente ColumnFilter
  const ColumnFilter = ({ columnName, title, filterKey, options, currentValue, sortKey, isDate = false }) => {
    return (
      <div className="relative" ref={el => dropdownRefs.current[columnName] = el}>
        <button
          onClick={() => toggleDropdown(columnName)}
          className="flex items-center justify-between w-full text-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
        >
          <span className="flex items-center gap-1">
            {title}
            {sortKey && (
              <span className="text-purple-600 font-bold">
                {getSortIcon(sortKey)}
              </span>
            )}
          </span>
          <ChevronDown 
            className={`w-3 h-3 ml-1 transition-transform ${
              activeDropdown === columnName ? 'rotate-180' : ''
            } ${currentValue ? 'text-purple-600' : ''}`}
          />
        </button>
        
        <AnimatePresence>
          {activeDropdown === columnName && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[250px]"
            >
              <div className="p-2 max-h-80 overflow-y-auto">
                {/* Opciones de ordenamiento */}
                {sortKey && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-100">
                      📊 Ordenar
                    </div>
                    <button
                      onClick={() => {
                        handleSort(sortKey);
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <span className="text-purple-600">↑</span>
                      A → Z (Ascendente)
                    </button>
                    <button
                      onClick={() => {
                        setSortConfig({ key: sortKey, direction: 'desc' });
                        setActiveDropdown(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                    >
                      <span className="text-purple-600">↓</span>
                      Z → A (Descendente)
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                  </>
                )}

                {/* Filtros de fecha personalizados */}
                {isDate && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      📅 Filtrar por Fechas
                    </div>
                    <div className="px-3 py-2">
                      <label className="block text-xs text-gray-600 mb-1">Fecha Desde:</label>
                      <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div className="px-3 py-2">
                      <label className="block text-xs text-gray-600 mb-1">Fecha Hasta:</label>
                      <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    {(filters.startDate || filters.endDate) && (
                      <button
                        onClick={() => {
                          handleFilterChange('startDate', '');
                          handleFilterChange('endDate', '');
                          setActiveDropdown(null);
                        }}
                        className="w-full mx-3 my-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Limpiar fechas
                      </button>
                    )}
                    <div className="border-t border-gray-100 my-1"></div>
                  </>
                )}

                {/* Opciones de filtrado predefinidas */}
                <div className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  🔍 Filtros Rápidos
                </div>
                <button
                  onClick={() => {
                    handleFilterChange(filterKey, '');
                    setActiveDropdown(null);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${
                    !currentValue ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {filterKey === 'product' ? '📦 Todos los productos' : 
                   filterKey === 'deliveredTo' ? '👥 Todos los destinatarios' :
                   filterKey === 'dateRange' ? '📅 Todas las fechas' :
                   '💰 Todos los costos'}
                </button>
                
                <div className="border-t border-gray-100 my-1"></div>
                
                {options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      handleFilterChange(filterKey, option.value);
                      setActiveDropdown(null);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded ${
                      currentValue === option.value ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <motion.div
      className="min-h-[calc(100vh-180px)] px-12 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="text-4xl font-extrabold text-gray-800 mb-8 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        Registro de Salidas
      </h2>

      {/* Barra de búsqueda */}
      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por producto, destinatario, vale, camión..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all duration-300 text-gray-700 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Contador de filtros activos */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex justify-center mb-4">
          <motion.button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Filter className="w-4 h-4" />
            {getActiveFiltersCount()} filtro{getActiveFiltersCount() > 1 ? 's' : ''} activo{getActiveFiltersCount() > 1 ? 's' : ''}
            <X className="w-4 h-4 ml-1" />
            Limpiar
          </motion.button>
        </div>
      )}

      <div className="flex justify-center gap-4 mb-8">
        <motion.button
          onClick={handleAddExit}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <PlusCircle className="w-5 h-5" />
          Nueva Salida
        </motion.button>
      </div>

      <AnimatePresence mode="wait">
        {showForm && (
          <ExitForm
            currentExit={editingExit}
            onSave={handleSaveExit}
            onCancel={handleCancelForm}
          />
        )}
      </AnimatePresence>

      {!showForm && (
        <motion.div
          className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-full mx-auto border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {currentExits.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <p className="text-xl font-medium">
                {searchTerm || getActiveFiltersCount() > 0 ? 'No se encontraron salidas que coincidan con los filtros.' : 'No hay salidas registradas. ¡Es hora de mover ese material!'}
              </p>
            </div>
          ) : (
            <div className="w-full">
              <div className="mb-4 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  📤 Salidas Registradas ({totalExits})
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Página {page} de {totalPages} • Mostrando {startIndex + 1}-{Math.min(endIndex, totalExits)} de {totalExits} salidas
                </p>
              </div>
              <div className="overflow-x-auto shadow-lg rounded-lg">
                <table className="w-full table-auto divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="id"
                          title="ID"
                          filterKey="id"
                          currentValue={filters.id}
                          options={[]}
                          sortKey="id"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="product"
                          title="Material"
                          filterKey="product"
                          currentValue={filters.product}
                          options={getUniqueProducts().map(product => ({ value: product, label: `📦 ${product}` }))}
                          sortKey="product_name"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <button
                          onClick={() => handleSort('quantity')}
                          className="flex items-center justify-center w-full text-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
                        >
                          <span className="flex items-center gap-1">
                            Cantidad
                            <span className="text-purple-600 font-bold">
                              {getSortIcon('quantity')}
                            </span>
                          </span>
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="cost"
                          title="Costo"
                          filterKey="costRange"
                          currentValue={filters.costRange}
                          options={[
                            { value: 'bajo', label: '💰 Bajo (< $100)' },
                            { value: 'medio', label: '💰 Medio ($100-$500)' },
                            { value: 'alto', label: '💰 Alto (> $500)' }
                          ]}
                          sortKey="unit_cost"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="date"
                          title="Fecha"
                          filterKey="dateRange"
                          currentValue={filters.dateRange}
                          options={[
                            { value: 'today', label: '📅 Hoy' },
                            { value: 'week', label: '📅 Esta semana' },
                            { value: 'month', label: '📅 Este mes' },
                            { value: 'year', label: '📅 Este año' }
                          ]}
                          sortKey="exit_date"
                          isDate={true}
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="deliveredTo"
                          title="Entregado A"
                          filterKey="deliveredTo"
                          currentValue={filters.deliveredTo}
                          options={getUniqueDeliveredTo().map(person => ({ value: person, label: `👥 ${person}` }))}
                          sortKey="delivered_to"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <button
                          onClick={() => handleSort('requisition_number')}
                          className="flex items-center justify-center w-full text-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
                        >
                          <span className="flex items-center gap-1">
                            Vale
                            <span className="text-purple-600 font-bold">
                              {getSortIcon('requisition_number')}
                            </span>
                          </span>
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <button
                          onClick={() => handleSort('truck')}
                          className="flex items-center justify-center w-full text-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
                        >
                          <span className="flex items-center gap-1">
                            Camión
                            <span className="text-purple-600 font-bold">
                              {getSortIcon('truck')}
                            </span>
                          </span>
                        </button>
                      </th>
                      <th scope="col" className="px-6 py-3 text-center">
                        <ColumnFilter
                          columnName="workshop"
                          title="Taller"
                          filterKey="workshop"
                          currentValue={filters.workshop}
                          options={getUniqueWorkshops().map(workshop => ({ value: workshop, label: `🔧 ${workshop}` }))}
                          sortKey="workshop"
                        />
                      </th>
                      {/* Columna de Acciones eliminada intencionalmente */}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <AnimatePresence>
                      {currentExits.map((exit) => (
                        <motion.tr
                          key={exit.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.3 }}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleViewExit(exit)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm font-medium text-gray-900">
                              #{exit.id}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-start">
                              <Box className="w-5 h-5 text-gray-500 mr-2 mt-0.5" />
                              <div className="flex-1">
                                {/* Título principal del renglón */}
                                <div className="text-sm font-medium text-gray-900">
                                  {exit.products && exit.products.length > 1
                                    ? 'Materiales'
                                    : (exit.product_name || (exit.products && exit.products.length > 0
                                        ? exit.products[0].product_name
                                        : 'Material no especificado'))}
                                </div>
                                {exit.product_sku && (
                                  <div className="text-xs text-gray-500">SKU: {exit.product_sku}</div>
                                )}
                                {/* Lista detallada de productos */}
                                {exit.products && exit.products.length > 0 && (
                                  <div className="mt-1 space-y-0.5">
                                    {exit.products.map((p, idx) => (
                                      <div
                                        key={idx}
                                        className="flex justify-between text-xs text-gray-700 bg-gray-50 rounded px-2 py-1"
                                      >
                                        <span className="truncate max-w-[55%]" title={p.product_name}>
                                          {p.product_name || 'Sin nombre'}
                                        </span>
                                        <span className="font-semibold whitespace-nowrap">
                                          {(p.quantity || 0)} {p.product_unit || p.unit || 'pzs'}
                                        </span>
                                        {p.unit_cost && (
                                          <span className="text-gray-500 whitespace-nowrap ml-2">
                                            ${parseFloat(p.unit_cost).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Si no hay arreglo de productos pero hay conteo */}
                                {(!exit.products || exit.products.length === 0) && exit.product_count > 0 && (
                                  <div className="text-xs text-gray-500">
                                    {exit.product_count} producto(s) diferentes
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-900 font-semibold">
                              {exit.products && exit.products.length > 0
                                ? exit.products.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0)
                                : (exit.quantity || 0)} {exit.product_unit || 'pzs'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="text-sm text-gray-900 font-medium">
                              ${exit.total_cost ? parseFloat(exit.total_cost).toFixed(2) : '0.00'}
                            </div>
                            {(exit.unit_cost || (exit.products && exit.products.length > 0 && exit.products[0].unit_cost)) && (
                              <div className="text-xs text-gray-500">
                                Unitario: ${parseFloat(exit.unit_cost || (exit.products && exit.products.length > 0 ? exit.products[0].unit_cost : 0)).toFixed(2)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center">
                              <Calendar className="w-4 h-4 text-gray-400 mr-1" />
                              <div className="text-sm text-gray-900">
                                {exit.exit_date ? (() => {
                                  const date = new Date(exit.exit_date);
                                  // Ajustar por zona horaria para mostrar la fecha correcta
                                  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                                  const localDate = new Date(date.getTime() + userTimezoneOffset);
                                  return localDate.toLocaleDateString();
                                })() : 'Sin fecha'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-400 mr-1" />
                              <div className="text-sm text-gray-900">
                                {exit.delivered_to || 'No especificado'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center">
                              <FileText className="w-4 h-4 text-gray-400 mr-1" />
                              <div className="text-sm text-gray-900">
                                {exit.requisition_number || 'Sin vale'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center">
                              <Truck className="w-4 h-4 text-gray-400 mr-1" />
                              <div className="text-sm text-gray-900">
                                {exit.truck || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center">
                              <Wrench className="w-4 h-4 text-gray-400 mr-1" />
                              <div className="text-sm text-gray-900">
                                {exit.workshop || 'N/A'}
                              </div>
                            </div>
                          </td>
                          {/* Celda de Acciones eliminada intencionalmente */}
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Paginación mejorada */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="flex items-center space-x-2">
                    <motion.button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        page === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-500 text-white hover:bg-purple-600 hover:scale-105'
                      }`}
                      whileHover={page !== 1 ? { scale: 1.05 } : {}}
                      whileTap={page !== 1 ? { scale: 0.95 } : {}}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </motion.button>
                    
                    <div className="flex items-center space-x-1">
                      {/* Primera página */}
                      <motion.button
                        onClick={() => handlePageChange(1)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          page === 1
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                        }`}
                        whileHover={page !== 1 ? { scale: 1.05 } : {}}
                        whileTap={{ scale: 0.95 }}
                      >
                        1
                      </motion.button>

                      {/* Puntos suspensivos izquierda */}
                      {page > 3 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}

                      {/* Páginas alrededor de la actual */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(pageNum => {
                          // Mostrar páginas cercanas a la actual (±1)
                          return pageNum > 1 && pageNum < totalPages && Math.abs(pageNum - page) <= 1;
                        })
                        .map(pageNum => (
                          <motion.button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              page === pageNum
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                            }`}
                            whileHover={page !== pageNum ? { scale: 1.05 } : {}}
                            whileTap={{ scale: 0.95 }}
                          >
                            {pageNum}
                          </motion.button>
                        ))
                      }

                      {/* Puntos suspensivos derecha */}
                      {page < totalPages - 2 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}

                      {/* Última página */}
                      {totalPages > 1 && (
                        <motion.button
                          onClick={() => handlePageChange(totalPages)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              page === totalPages
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                            }`}
                            whileHover={page !== totalPages ? { scale: 1.05 } : {}}
                            whileTap={{ scale: 0.95 }}
                        >
                          {totalPages}
                        </motion.button>
                      )}
                    </div>
                    
                    <motion.button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        page === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-500 text-white hover:bg-purple-600 hover:scale-105'
                      }`}
                      whileHover={page !== totalPages ? { scale: 1.05 } : {}}
                      whileTap={page !== totalPages ? { scale: 0.95 } : {}}
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ExitsPage;